import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { getStoreInfo } from '../../api/stores';
import { getKnowledgeBases, createKnowledgeBase, updateKnowledgeBase, deleteKnowledgeBase } from '../../api/knowledgeBases';


const PRESETS = [
    { q: '영업시간이 어떻게 되나요?',       a: '' },
    { q: '주차가 가능한가요?',               a: '' },
    { q: '가격이 어떻게 되나요?',            a: '' },
    { q: '예약은 어떻게 하나요?',            a: '' },
    { q: '위치가 어디인가요?',               a: '' },
    { q: '당일 예약이 가능한가요?',          a: '' },
    { q: '취소나 변경은 어떻게 하나요?',     a: '' },
    { q: '어떤 서비스를 제공하시나요?',      a: '' },
];

let nextId = 1;
const makeItem = (uuid = null, q = '', a = '', keywords = []) => ({
    id: nextId++,
    uuid,
    q,
    a,
    keywords,
    saving: false,
    saveError: null,
});


const KnowledgeBasesPage = () => {
    const { uuid } = useParams();
    const navigate = useNavigate();

    const [스토어명, set스토어명] = useState('');
    const [로딩중, set로딩중] = useState(true);
    const [에러, set에러] = useState(null);
    const [items, setItems] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [keywordDraft, setKeywordDraft] = useState('');
    const [삭제중UUID, set삭제중UUID] = useState(null);
    const keywordInputRef = useRef(null);

    useEffect(() => {
        Promise.all([getStoreInfo(uuid), getKnowledgeBases(uuid)])
            .then(([스토어, kbs]) => {
                set스토어명(스토어?.name ?? '');
                setItems(
                    kbs.map((kb) =>
                        makeItem(
                            kb.uuid,
                            kb.question,
                            kb.answer,
                            kb.keywords
                                ? kb.keywords.split(',').map((k) => k.trim()).filter(Boolean)
                                : []
                        )
                    )
                );
            })
            .catch(() => set에러('정보를 불러오지 못했습니다.'))
            .finally(() => set로딩중(false));
    }, [uuid]);

    // editingId 바뀔 때 키워드 초안 초기화
    useEffect(() => { setKeywordDraft(''); }, [editingId]);

    const updateItem = (id, field, value) =>
        setItems((prev) => prev.map((it) => it.id === id ? { ...it, [field]: value } : it));

    const addPreset = (preset) => {
        if (items.some((it) => it.q === preset.q)) return;
        const item = makeItem(null, preset.q, preset.a);
        setItems((prev) => [...prev, item]);
        setEditingId(item.id);
    };

    const addBlank = () => {
        const item = makeItem();
        setItems((prev) => [...prev, item]);
        setEditingId(item.id);
    };

    const handleSave = async (item) => {
        updateItem(item.id, 'saving', true);
        updateItem(item.id, 'saveError', null);

        const body = {
            question: item.q,
            answer: item.a,
            keywords: item.keywords.length ? item.keywords.join(', ') : undefined,
            priority: 0,
        };

        try {
            if (item.uuid) {
                await updateKnowledgeBase(uuid, item.uuid, body);
            } else {
                const result = await createKnowledgeBase(uuid, body);
                setItems((prev) =>
                    prev.map((it) => it.id === item.id ? { ...it, uuid: result?.uuid ?? result?.id ?? null } : it)
                );
            }
            setEditingId(null);
        } catch {
            updateItem(item.id, 'saveError', '저장 중 오류가 발생했습니다.');
        } finally {
            updateItem(item.id, 'saving', false);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm('이 항목을 삭제하시겠습니까?')) return;
        if (!item.uuid) {
            setItems((prev) => prev.filter((it) => it.id !== item.id));
            if (editingId === item.id) setEditingId(null);
            return;
        }
        set삭제중UUID(item.uuid);
        try {
            await deleteKnowledgeBase(uuid, item.uuid);
            setItems((prev) => prev.filter((it) => it.id !== item.id));
            if (editingId === item.id) setEditingId(null);
        } catch {
            alert('삭제 중 오류가 발생했습니다.');
        } finally {
            set삭제중UUID(null);
        }
    };

    const handleKeywordKeyDown = (e, item) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            const kw = keywordDraft.replace(/,/g, '').trim();
            if (kw && !item.keywords.includes(kw)) {
                updateItem(item.id, 'keywords', [...item.keywords, kw]);
            }
            setKeywordDraft('');
        } else if (e.key === 'Backspace' && !keywordDraft && item.keywords.length > 0) {
            updateItem(item.id, 'keywords', item.keywords.slice(0, -1));
        }
    };

    const removeKeyword = (item, kw) => {
        updateItem(item.id, 'keywords', item.keywords.filter((k) => k !== kw));
    };

    const savedCount = items.filter((it) => it.uuid).length;

    const handleDownload = () => {
        const saved = items.filter((it) => it.uuid);
        if (saved.length === 0) return;
        const lines = saved.map((it, i) => {
            const parts = [
                `Q${i + 1}. ${it.q}`,
                `A. ${it.a}`,
            ];
            if (it.keywords.length > 0) parts.push(`키워드: ${it.keywords.join(', ')}`);
            return parts.join('\n');
        });
        const text = `[${스토어명}] 자주묻는 질문\n총 ${saved.length}개 항목\n\n` + lines.join('\n\n---\n\n');
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${스토어명}_자주묻는질문.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (로딩중) return (
        <Page>
            <Body><Inner><StatusText>불러오는 중...</StatusText></Inner></Body>
        </Page>
    );

    if (에러) return (
        <Page>
            <Body><Inner><StatusText $error>{에러}</StatusText></Inner></Body>
        </Page>
    );

    return (
        <Page>
            <Body>
                <Inner>
                    {/* Back */}
                    <BackButton onClick={() => navigate('/stores')}>
                        ← 스토어 목록
                    </BackButton>

                    {/* Title */}
                    <TitleArea>
                        <PageBadge>자주묻는 질문</PageBadge>
                        <PageTitle>{스토어명 || '스토어'}</PageTitle>
                        <PageDesc>
                            AI가 고객 질문에 답변할 때 이 내용을 참고합니다.<br />
                            질문과 답변, 그리고 관련 키워드를 함께 등록하면 더 정확하게 응대해요.
                        </PageDesc>
                    </TitleArea>

                    {/* Preset chips */}
                    <ChipSection>
                        <ChipLabel>빠르게 추가하기</ChipLabel>
                        <ChipList>
                            {PRESETS.map((p) => {
                                const added = items.some((it) => it.q === p.q);
                                return (
                                    <Chip
                                        key={p.q}
                                        $added={added}
                                        onClick={() => !added && addPreset(p)}
                                        disabled={added}
                                    >
                                        {added ? '✓ ' : '+ '}{p.q}
                                    </Chip>
                                );
                            })}
                        </ChipList>
                    </ChipSection>

                    {/* KB List */}
                    <KbList>
                        {items.map((item, idx) => {
                            const isEditing = editingId === item.id;
                            const isDeleting = 삭제중UUID === item.uuid;
                            return (
                                <KbCard
                                    key={item.id}
                                    $editing={isEditing}
                                    onClick={() => !isEditing && setEditingId(item.id)}
                                >
                                    <KbCardTop>
                                        <KbIndex>Q{idx + 1}</KbIndex>
                                        <DeleteBtn
                                            onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                                            disabled={isDeleting}
                                            title="삭제"
                                        >
                                            {isDeleting ? '...' : '✕'}
                                        </DeleteBtn>
                                    </KbCardTop>

                                    {isEditing ? (
                                        <KbInputs onClick={(e) => e.stopPropagation()}>
                                            <QuestionInput
                                                placeholder="질문을 입력하세요  예: 영업시간이 어떻게 되나요?"
                                                value={item.q}
                                                onChange={(e) => updateItem(item.id, 'q', e.target.value)}
                                                autoFocus
                                            />
                                            <AnswerTextarea
                                                placeholder="답변을 입력하세요  예: 평일 오전 10시부터 오후 8시까지 운영합니다."
                                                value={item.a}
                                                onChange={(e) => updateItem(item.id, 'a', e.target.value)}
                                                rows={3}
                                            />

                                            {/* 키워드 태그 입력 */}
                                            <KeywordField>
                                                <KeywordFieldLabel>키워드 <span>(선택)</span></KeywordFieldLabel>
                                                <KeywordInputRow
                                                    onClick={() => keywordInputRef.current?.focus()}
                                                >
                                                    {item.keywords.map((kw) => (
                                                        <KeywordChip key={kw}>
                                                            {kw}
                                                            <KeywordChipX
                                                                onClick={(e) => { e.stopPropagation(); removeKeyword(item, kw); }}
                                                            >✕</KeywordChipX>
                                                        </KeywordChip>
                                                    ))}
                                                    <KeywordTextInput
                                                        ref={keywordInputRef}
                                                        placeholder={item.keywords.length === 0 ? '예: 영업, 오픈, 운영시간  — Enter로 추가' : ''}
                                                        value={keywordDraft}
                                                        onChange={(e) => setKeywordDraft(e.target.value)}
                                                        onKeyDown={(e) => handleKeywordKeyDown(e, item)}
                                                        onBlur={() => {
                                                            const kw = keywordDraft.replace(/,/g, '').trim();
                                                            if (kw && !item.keywords.includes(kw)) {
                                                                updateItem(item.id, 'keywords', [...item.keywords, kw]);
                                                            }
                                                            setKeywordDraft('');
                                                        }}
                                                    />
                                                </KeywordInputRow>
                                            </KeywordField>

                                            {item.saveError && <SaveError>{item.saveError}</SaveError>}

                                            <SaveRow>
                                                <CancelBtn onClick={() => {
                                                    if (!item.uuid) {
                                                        setItems((prev) => prev.filter((it) => it.id !== item.id));
                                                    }
                                                    setEditingId(null);
                                                }}>
                                                    취소
                                                </CancelBtn>
                                                <SaveBtn
                                                    onClick={() => handleSave(item)}
                                                    disabled={!item.q.trim() || !item.a.trim() || item.saving}
                                                >
                                                    {item.saving ? '저장 중...' : '저장'}
                                                </SaveBtn>
                                            </SaveRow>
                                        </KbInputs>
                                    ) : (
                                        <KbPreview>
                                            <KbQ $empty={!item.q}>{item.q || '질문을 입력해주세요'}</KbQ>
                                            <KbA $empty={!item.a}>{item.a || '답변을 입력해주세요'}</KbA>
                                            {item.keywords.length > 0 && (
                                                <KeywordTags>
                                                    {item.keywords.map((kw) => (
                                                        <KeywordTag key={kw}>{kw}</KeywordTag>
                                                    ))}
                                                </KeywordTags>
                                            )}
                                            {!item.uuid && (
                                                <UnsavedBadge>미저장</UnsavedBadge>
                                            )}
                                        </KbPreview>
                                    )}
                                </KbCard>
                            );
                        })}

                        <AddButton onClick={addBlank}>+ 직접 추가하기</AddButton>
                    </KbList>

                    {/* 하단 */}
                    <BottomArea>
                        <CountText>
                            {savedCount > 0
                                ? <><Strong>{savedCount}개</Strong> 항목이 등록됐어요</>
                                : '항목을 1개 이상 등록하면 AI가 더 잘 응대할 수 있어요'
                            }
                        </CountText>
                        {savedCount > 0 && (
                            <DownloadButton onClick={handleDownload}>
                                TXT 다운로드
                            </DownloadButton>
                        )}
                    </BottomArea>
                </Inner>
            </Body>
        </Page>
    );
};


/* ─────────────────────────────────────────────
   Styled Components
───────────────────────────────────────────── */
const Page = styled.div`
    width: 100%;
    min-height: 100dvh;
    background: #f7f8fa;
    font-family: Pretendard, -apple-system, sans-serif;
`;

const Body = styled.div`
    padding-top: 80px;
    padding-bottom: 60px;
    display: flex;
    justify-content: center;
`;

const Inner = styled.div`
    width: 100%;
    max-width: 640px;
    padding: 0 16px;
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

const BackButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #aaaaaa;
    cursor: pointer;
    align-self: flex-start;
    &:hover { color: #333333; }
`;

const TitleArea = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const PageBadge = styled.span`
    font-size: 11px;
    font-weight: 700;
    color: #487BFD;
    letter-spacing: 0.1em;
    text-transform: uppercase;
`;

const PageTitle = styled.h1`
    font-size: 24px;
    font-weight: 800;
    color: #111111;
    letter-spacing: -0.02em;
    margin: 0;
`;

const PageDesc = styled.p`
    font-size: 14px;
    color: #999999;
    line-height: 1.65;
    margin: 0;
`;

/* Chips */
const ChipSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ChipLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: #aaaaaa;
`;

const ChipList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const Chip = styled.button`
    background: ${({ $added }) => $added ? '#eef2ff' : '#ffffff'};
    border: 1px solid ${({ $added }) => $added ? '#c0d0ff' : '#e0e0e0'};
    border-radius: 100px;
    padding: 7px 14px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    color: ${({ $added }) => $added ? '#487BFD' : '#555555'};
    cursor: ${({ $added }) => $added ? 'default' : 'pointer'};
    transition: border-color 0.15s, background 0.15s, color 0.15s;
    &:hover:not(:disabled) { border-color: #487BFD; color: #487BFD; background: #f5f8ff; }
`;

/* KB List */
const KbList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const KbCard = styled.div`
    background: #ffffff;
    border: 1.5px solid ${({ $editing }) => $editing ? '#487BFD' : '#eeeeee'};
    border-radius: 16px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    cursor: ${({ $editing }) => $editing ? 'default' : 'pointer'};
    transition: border-color 0.15s, box-shadow 0.15s;
    box-shadow: ${({ $editing }) => $editing ? '0 0 0 4px rgba(72,123,253,0.08)' : 'none'};
    &:hover { border-color: ${({ $editing }) => $editing ? '#487BFD' : '#cccccc'}; }
`;

const KbCardTop = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const KbIndex = styled.span`
    font-size: 11px;
    font-weight: 700;
    color: #487BFD;
    letter-spacing: 0.06em;
`;

const DeleteBtn = styled.button`
    background: none;
    border: none;
    font-size: 13px;
    color: #cccccc;
    cursor: pointer;
    padding: 2px 4px;
    line-height: 1;
    transition: color 0.15s;
    &:hover:not(:disabled) { color: #EF4444; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

/* Editing */
const KbInputs = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const inputBase = `
    width: 100%;
    box-sizing: border-box;
    background: #f7f8fa;
    border: 1px solid #e8e8e8;
    border-radius: 10px;
    padding: 10px 14px;
    font-family: Pretendard;
    font-size: 14px;
    color: #111111;
    outline: none;
    resize: none;
    transition: border-color 0.15s;
    &::placeholder { color: #c0c0c0; }
    &:focus { border-color: #487BFD; background: #ffffff; }
`;

const QuestionInput = styled.input`${inputBase}`;
const AnswerTextarea = styled.textarea`${inputBase}`;

/* 키워드 필드 */
const KeywordField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const KeywordFieldLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: #aaaaaa;
    span { font-weight: 400; }
`;

const KeywordInputRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    background: #f7f8fa;
    border: 1px solid #e8e8e8;
    border-radius: 10px;
    padding: 8px 12px;
    cursor: text;
    transition: border-color 0.15s;
    &:focus-within { border-color: #487BFD; background: #ffffff; }
`;

const KeywordChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #eef2ff;
    border: 1px solid #c0d0ff;
    border-radius: 100px;
    padding: 3px 10px;
    font-size: 12px;
    font-weight: 500;
    color: #487BFD;
    white-space: nowrap;
`;

const KeywordChipX = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-size: 10px;
    color: #487BFD;
    cursor: pointer;
    line-height: 1;
    opacity: 0.6;
    &:hover { opacity: 1; }
`;

const KeywordTextInput = styled.input`
    border: none;
    outline: none;
    background: transparent;
    font-family: Pretendard;
    font-size: 13px;
    color: #111111;
    flex: 1;
    min-width: 120px;
    &::placeholder { color: #c0c0c0; }
`;

const SaveError = styled.span`
    font-size: 12px;
    color: #EF4444;
`;

const SaveRow = styled.div`
    display: flex;
    gap: 8px;
    justify-content: flex-end;
`;

const CancelBtn = styled.button`
    background: none;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    padding: 7px 16px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    color: #aaaaaa;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
    &:hover { border-color: #bbbbbb; color: #555555; }
`;

const SaveBtn = styled.button`
    background: #487BFD;
    border: none;
    border-radius: 8px;
    padding: 7px 20px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: opacity 0.15s;
    &:hover:not(:disabled) { opacity: 0.85; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

/* Preview */
const KbPreview = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const KbQ = styled.p`
    font-size: 14px;
    font-weight: 600;
    color: ${({ $empty }) => $empty ? '#cccccc' : '#111111'};
    margin: 0;
    font-style: ${({ $empty }) => $empty ? 'italic' : 'normal'};
`;

const KbA = styled.p`
    font-size: 13px;
    color: ${({ $empty }) => $empty ? '#cccccc' : '#777777'};
    margin: 0;
    line-height: 1.6;
    font-style: ${({ $empty }) => $empty ? 'italic' : 'normal'};
`;

const KeywordTags = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 2px;
`;

const KeywordTag = styled.span`
    background: #f0f3ff;
    border: 1px solid #dce6ff;
    border-radius: 100px;
    padding: 2px 10px;
    font-size: 11px;
    font-weight: 500;
    color: #7090dd;
`;

const UnsavedBadge = styled.span`
    align-self: flex-start;
    font-size: 10px;
    font-weight: 600;
    color: #f59e0b;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 4px;
    padding: 1px 6px;
    margin-top: 2px;
`;

const AddButton = styled.button`
    background: #ffffff;
    border: 1.5px dashed #d8d8d8;
    border-radius: 16px;
    padding: 16px;
    width: 100%;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #aaaaaa;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
    &:hover { border-color: #487BFD; color: #487BFD; background: #f5f8ff; }
`;

/* Bottom */
const BottomArea = styled.div`
    padding-top: 8px;
    padding-bottom: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
`;

const CountText = styled.p`
    font-size: 13px;
    color: #aaaaaa;
    margin: 0;
    text-align: center;
`;

const DownloadButton = styled.button`
    background: none;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 8px 18px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    color: #888888;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
    &:hover { border-color: #487BFD; color: #487BFD; }
`;

const Strong = styled.strong`
    color: #487BFD;
    font-weight: 700;
`;

const StatusText = styled.span`
    font-family: Pretendard;
    font-size: 15px;
    color: ${({ $error }) => $error ? '#EF4444' : '#888888'};
`;


export default KnowledgeBasesPage;
